//Frontend/src/unicodex_ui/pages/PosTerminalPage.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import ManufacturerNav from "../components/ManufacturerNav";
import portalPosApi from "../services/portalPosApi";
import "../styles/PortalBusiness.css";
import { getStoredPermissions, getStoredPortalUser, clearPortalSession } from "../services/authApi";
import { useNavigate } from "react-router-dom";

const PAYMENT_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "card", label: "Card" },
  { value: "mobile", label: "Mobile" },
  { value: "efos", label: "EFOS" },
  { value: "transfer", label: "Transfer" },
  { value: "other", label: "Other" },
];

function round2(n) {
  const x = Number(n || 0);
  return Math.round(x * 100) / 100;
}

function calcTotals(items, discountTotal = 0) {
  const subtotal = items.reduce((s, it) => s + round2(it.quantity) * round2(it.unitPrice), 0);
  const taxTotal = items.reduce((s, it) => s + round2(it.quantity) * round2(it.unitPrice) * round2(it.taxRate || 0), 0);
  const disc = round2(discountTotal);
  const grand = round2(subtotal + taxTotal - disc);
  return { subtotal: round2(subtotal), taxTotal: round2(taxTotal), discountTotal: disc, grandTotal: grand };
}

function looksLikeBarcode(raw) {
  const v = String(raw || "").trim();
  if (!/^\d+$/.test(v)) return false;
  return v.length === 12 || v.length === 13;
}

export default function PosTerminalPage() {
  const navigate = useNavigate();
  const perms = getStoredPermissions();
  const user = getStoredPortalUser();
  const isCashierRole = String(user?.role || "").toLowerCase() === "cashier";

  const canOpenShift = perms.includes("pos.shift.open");
  const canCloseShift = perms.includes("pos.shift.close");
  const canCreateSale = perms.includes("pos.sale.create");
  const canReadSales = perms.includes("pos.sale.read");
  const canVoid = perms.includes("pos.sale.void");
  const canRefund = perms.includes("pos.sale.refund");
  // Supervisor = cashier + override powers (void/refund/close). Not just "read".
  const isSupervisor = canVoid || canRefund || canCloseShift;

  const links = useMemo(
    () => [
      { to: "/portal/home", label: "Home" },
      { to: "/portal/pos", label: "POS" },
      { to: "/portal/purchasing", label: "Purchasing", perm: "purchasing.po.read" },
      { to: "/portal/inventory", label: "Inventory", perm: "inventory.stock.levels.read" },
    ],
    []
  );

  const [branches, setBranches] = useState([]);
  const [terminals, setTerminals] = useState([]);

  const [branchId, setBranchId] = useState("");
  const [terminalId, setTerminalId] = useState("");

  const [openShift, setOpenShift] = useState(null);
  const [loadingShift, setLoadingShift] = useState(false);

  const [openingNote, setOpeningNote] = useState("");
  const [closingNote, setClosingNote] = useState("");

  const [productQuery, setProductQuery] = useState("");
  const [productResults, setProductResults] = useState([]);
  const [productLoading, setProductLoading] = useState(false);
  const productReqSeq = useRef(0);
  const [topLoaded, setTopLoaded] = useState(false);

  const [items, setItems] = useState([]);
  const [discountTotal, setDiscountTotal] = useState(0);

  const totals = useMemo(() => calcTotals(items, discountTotal), [items, discountTotal]);

  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [paymentAmount, setPaymentAmount] = useState(0);

  const [stockWarnings, setStockWarnings] = useState({}); // key: idx -> { ok, allowed, available, warning }
  const [sales, setSales] = useState([]);
  const [salesLoading, setSalesLoading] = useState(false);

  // Shift summary (for cashier receipt + summary card)
  const [shiftSummary, setShiftSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Supervisor receipt filters
  const [receiptFilters, setReceiptFilters] = useState({
    mode: "shift", // shift | date | cashier | terminal | all
    from: "",
    to: "",
    cashierUserId: "",
    terminalId: "",
    shiftId: "",
    includeItems: true,
    includePayments: true,
  });

  const canCheckout = useMemo(() => {
    if (!openShift?.id) return false;
    if (!branchId || !terminalId) return false;
    if (!items.length) return false;
    if (!canCreateSale) return false;
    if (round2(paymentAmount) < round2(totals.grandTotal)) return false;

    // hard block if any line is explicitly not allowed
    const anyBlocked = Object.values(stockWarnings).some((w) => w && w.ok && w.allowed === false);
    if (anyBlocked) return false;

    return true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openShift, branchId, terminalId, items.length, paymentAmount, totals.grandTotal, stockWarnings]);

  // Load branches on mount
  useEffect(() => {
    (async () => {
      try {
        const data = await portalPosApi.listBranches();
        setBranches(Array.isArray(data?.branches) ? data.branches : []);
      } catch {
        setBranches([]);
      }
    })();
  }, []);

  // Load terminals when branch changes
  useEffect(() => {
    if (!branchId) {
      setTerminals([]);
      setTerminalId("");
      return;
    }
    (async () => {
      try {
        const data = await portalPosApi.listTerminals(Number(branchId));
        setTerminals(Array.isArray(data?.terminals) ? data.terminals : []);
      } catch {
        setTerminals([]);
      }
    })();
  }, [branchId]);

  // Load open shift when branch+terminal selected
  useEffect(() => {
    if (!branchId || !terminalId) {
      setOpenShift(null);
      return;
    }
    (async () => {
      setLoadingShift(true);
      try {
        const data = await portalPosApi.getOpenShift({
          branchId: Number(branchId),
          terminalId: Number(terminalId),
        });
        setOpenShift(data?.shift || null);
      } catch {
        setOpenShift(null);
      } finally {
        setLoadingShift(false);
      }
    })();
  }, [branchId, terminalId]);

  // Load top 5 products when shift is open (initial list for cashier)
  useEffect(() => {
    if (!openShift?.id || !branchId || !canCreateSale) {
      setTopLoaded(false);
      return;
    }
    if (topLoaded) return;

    const seq = ++productReqSeq.current;
    (async () => {
      setProductLoading(true);
      try {
        const data = await portalPosApi.searchProducts({ branchId, search: "", limit: 5, offset: 0 });
        if (productReqSeq.current !== seq) return;
        setProductResults(Array.isArray(data?.items) ? data.items : []);
        setTopLoaded(true);
      } catch {
        if (productReqSeq.current !== seq) return;
        // If query cleared, keep showing top list (don’t wipe).
      } finally {
        if (productReqSeq.current === seq) setProductLoading(false);
      }
    })();
  }, [openShift?.id, branchId, canCreateSale, topLoaded]);

  // Keep payment default to grand total when items change (unless user edits)
  useEffect(() => {
    setPaymentAmount((prev) => (prev ? prev : totals.grandTotal));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totals.grandTotal]);

  // Search products (debounced)
  useEffect(() => {
    const q = productQuery.trim();
    if (!q) {
      setProductResults([]);
      return;
    }

    const seq = ++productReqSeq.current;
    const t = setTimeout(async () => {
      setProductLoading(true);
      try {
        const data = await portalPosApi.searchProducts({ branchId, search: q, limit: 20, offset: 0 });
        if (productReqSeq.current !== seq) return;
        // portalPosApi normalizes to { items } (see diff in portalPosApi.js)
        setProductResults(Array.isArray(data?.items) ? data.items : []);
      } catch {
        if (productReqSeq.current !== seq) return;
        setProductResults([]);
      } finally {
        if (productReqSeq.current === seq) setProductLoading(false);
      }
    }, 250);

    return () => clearTimeout(t);
  }, [productQuery, branchId]);

  async function refreshSales() {
    if (!branchId) return;
    // Cashier must not see sales list even if permissions were mistakenly granted.
    if (!canReadSales || isCashierRole) {
      setSales([]);
      return;
    }
    setSalesLoading(true);
    try {
      const data = await portalPosApi.listSales({
        branchId: Number(branchId),
        shiftId: openShift?.id || undefined,
        limit: 10,
        offset: 0,
      });
      setSales(Array.isArray(data?.sales) ? data.sales : []);
    } catch {
      setSales([]);
    } finally {
      setSalesLoading(false);
    }
  }

  async function refreshShiftSummary() {
    if (!openShift?.id) {
      setShiftSummary(null);
      return;
    }
    setSummaryLoading(true);
    try {
      const data = await portalPosApi.getShiftSummary(openShift.id);
      setShiftSummary(data?.summary || null);
    } catch {
      setShiftSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }

    async function tryResolveBarcodeAndAdd(raw) {
    const value = String(raw || "").trim();
    if (!looksLikeBarcode(value)) return false;
    if (!branchId) return false;

    try {
      const data = await portalPosApi.resolveByBarcode({ branchId: Number(branchId), value });
      if (data?.ok && data?.product) {
        addProduct(data.product);
        return true;
      }
    } catch {
      // swallow; fallback to normal search
    }
    return false;
  }


  async function onVoidSale(saleId) {
    const reason = window.prompt("Void reason:");
    if (!reason) return;
    try {
      const data = await portalPosApi.voidSale(saleId, reason);
      if (!data?.ok) return alert("Unable to void sale.");
      await refreshSales();
      alert("Sale voided (stock returned).");
    } catch {
      alert("Unable to void sale.");
    }
  }

  async function onRefundSale(saleId) {
    const reason = window.prompt("Refund reason:");
    if (!reason) return;
    try {
      const data = await portalPosApi.refundSale(saleId, reason);
      if (!data?.ok) return alert("Unable to refund sale.");
      await refreshSales();
      alert("Sale refunded (stock returned).");
    } catch {
      alert("Unable to refund sale.");
    }
  }

  useEffect(() => {
    refreshSales();
    refreshShiftSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, openShift?.id]);

  function addProduct(p) {
    // POS products come from DB with snake_case: unit_price
    // Normalize to the cart's camelCase: unitPrice
    const price =
      p?.unitPrice !== undefined && p?.unitPrice !== null
        ? Number(p.unitPrice)
        : p?.unit_price !== undefined && p?.unit_price !== null
          ? Number(p.unit_price)
          : 0;

    const tax =
       p?.taxRate !== undefined && p?.taxRate !== null
         ? Number(p.taxRate)
         : p?.tax_rate !== undefined && p?.tax_rate !== null
           ? Number(p.tax_rate)
           : 0;
    setItems((prev) => [
      ...prev,
      {
        productId: p.id,
        productLabel: `${p.name || "Product"} (ID: ${p.id})`,
        batchId: "",
        quantity: 1,
        unitPrice: Number.isFinite(price) ? price : 0,
        taxRate: Number.isFinite(tax) ? tax : 0,
      },
    ]);
    setProductQuery("");
    setProductResults([]);
  }

  function updateItem(idx, patch) {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }

  function removeItem(idx) {
    setItems((prev) => prev.filter((_, i) => i !== idx));
    setStockWarnings((prev) => {
      const next = { ...prev };
      delete next[idx];
      return next;
    });
  }

  async function checkStockForLine(idx, it) {
    if (!branchId || !it?.productId || !it?.quantity) return;

    try {
      const data = await portalPosApi.stockCheck({
        branchId: Number(branchId),
        productId: Number(it.productId),
        batchId: it.batchId ? Number(it.batchId) : undefined,
        quantity: Number(it.quantity),
      });

      setStockWarnings((prev) => ({
        ...prev,
        [idx]: data,
      }));
    } catch {
      // if stock check fails, do not block by default; show soft warning
      setStockWarnings((prev) => ({
        ...prev,
        [idx]: { ok: false, error: "stock_check_failed" },
      }));
    }
  }

  async function onOpenShift() {
    if (!branchId || !terminalId) return;
    if (!canOpenShift) return alert("Not allowed to open shifts.");
    try {
      const data = await portalPosApi.openShift({
        branchId: Number(branchId),
        terminalId: Number(terminalId),
        openingNote: openingNote || null,
      });
      if (data?.ok) {
        setOpenShift(data.shift);
        setOpeningNote("");
        await refreshShiftSummary();
      } else {
        alert("Unable to open shift.");
      }
    } catch {
      alert("Unable to open shift.");
    }
  }

  async function onCloseShift() {
    if (!openShift?.id) return;
    if (!canCloseShift) return alert("Not allowed to close shifts.")
    try {
      const data = await portalPosApi.closeShift(openShift.id, { closingNote: closingNote || null });
      if (data?.ok) {
        setOpenShift(null);
        setClosingNote("");
        await refreshSales();
        setShiftSummary(null);
      } else {
        alert("Unable to close shift.");
      }
    } catch {
      alert("Unable to close shift.");
    }
  }

  async function onCheckout() {
    if (!canCheckout) return;

    const payload = {
      branchId: Number(branchId),
      terminalId: Number(terminalId),
      shiftId: Number(openShift.id),
      currency: "USD",
      discountTotal: round2(discountTotal),
      items: items.map((it) => ({
        productId: Number(it.productId),
        batchId: it.batchId ? Number(it.batchId) : null,
        quantity: Number(it.quantity),
        unitPrice: round2(it.unitPrice),
        // Cashier must not be able to override tax; backend enforces from DB anyway.
        ...(isCashierRole
          ? {}
          : { taxRate: round2(it.taxRate || 0) }),
      })),
      payments: [
        {
          method: paymentMethod,
          amount: round2(paymentAmount),
          reference: null,
        },
      ],
    };

    try {
      const data = await portalPosApi.createSale(payload);
      if (!data?.ok) {
        alert("Unable to create sale.");
        return;
      }

      // reset cart
      setItems([]);
      setDiscountTotal(0);
      setStockWarnings({});
      setPaymentAmount(0);
      await refreshSales();
      await refreshShiftSummary();
      alert("Sale recorded.");
    } catch {
      alert("Unable to create sale.");
    }
  }

  function formatMoney(n) {
    const x = Number(n || 0);
    return x.toFixed(2);
  }

  function openPrintWindow(html, title = "Receipt") {
    const w = window.open("", "_blank", "noopener,noreferrer,width=720,height=900");
    if (!w) return alert("Popup blocked. Allow popups to print receipts.");
    w.document.open();
    w.document.write(`<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width,initial-scale=1" />
    <title>${title}</title>
    <style>
      body { font-family: Arial, sans-serif; padding: 16px; color: #111; }
      .muted { color: #555; font-size: 12px; }
      h1 { font-size: 16px; margin: 0 0 8px; }
      h2 { font-size: 13px; margin: 16px 0 6px; }
      table { width:100%; border-collapse: collapse; }
      td, th { border-bottom: 1px solid #ddd; padding: 6px 4px; font-size: 12px; text-align:left; }
      .row { display:flex; justify-content:space-between; gap:12px; font-size: 12px; padding: 2px 0; }
      .strong { font-weight: 700; }
      @media print { button { display:none; } }
    </style>
  </head>
  <body>
    ${html}
    <script>window.focus();window.print();</script>
  </body>
</html>`);
    w.document.close();
  }

  async function onPrintActiveShiftReceipt() {
    if (!openShift?.id) return alert("No open shift.");
    await refreshShiftSummary();
    const s = shiftSummary;
    if (!s) return alert("Shift summary not available.");

    const html = `
      <h1>POS Receipt — Active Shift Summary</h1>
      <div class="muted">Filter: Active shift only</div>
      <div class="muted">Branch #${branchId} • Terminal #${terminalId}</div>
      <div class="muted">Cashier: ${user?.email || "-"} (${user?.id || "-"})</div>
      <div class="muted">Shift #${openShift.id}</div>
      <h2>Totals</h2>
      <div class="row"><span>Sales count</span><span class="strong">${s.salesCount ?? "-"}</span></div>
      <div class="row"><span>Gross</span><span class="strong">${formatMoney(s.grossTotal)}</span></div>
      <div class="row"><span>Tax</span><span class="strong">${formatMoney(s.taxTotal)}</span></div>
      <div class="row"><span>Discount</span><span class="strong">${formatMoney(s.discountTotal)}</span></div>
      <div class="row"><span>Net</span><span class="strong">${formatMoney(s.netTotal)}</span></div>
      <div class="row"><span>Refunded</span><span class="strong">${formatMoney(s.refundTotal)}</span></div>
      <div class="row"><span>Voided</span><span class="strong">${formatMoney(s.voidTotal)}</span></div>
    `;
    openPrintWindow(html, "Shift Receipt");
  }

  async function onPrintSupervisorReceipt() {
    if (!isSupervisor) return;
    if (!branchId) return alert("Select a branch first.");

    const f = receiptFilters;
    const params = {
      branchId: Number(branchId),
      limit: 500,
      offset: 0,
    };

    let label = "All sales";

    if (f.mode === "shift") {
      const sid = f.shiftId || openShift?.id;
      if (!sid) return alert("Pick a shift.");
      params.shiftId = Number(sid);
      label = `By shift: ${sid}`;
    } else if (f.mode === "terminal") {
      const tid = f.terminalId || terminalId;
      if (!tid) return alert("Pick a terminal.");
      params.terminalId = Number(tid);
      label = `By terminal: ${tid}`;
    } else if (f.mode === "cashier") {
      if (!f.cashierUserId) return alert("Enter cashier user id.");
      params.soldByUserId = Number(f.cashierUserId);
      label = `By cashier: ${f.cashierUserId}`;
    } else if (f.mode === "date") {
      if (!f.from || !f.to) return alert("Enter from/to.");
      params.from = f.from;
      params.to = f.to;
      label = `By date range: ${f.from} → ${f.to}`;
    }

    try {
      const data = await portalPosApi.listSalesForReceipt(params);
      const list = Array.isArray(data?.sales) ? data.sales : [];

      const total = list.reduce((s, x) => s + Number(x.grand_total || 0), 0);

      const rows = list
        .map((x) => {
          const when = x.created_at ? new Date(x.created_at).toLocaleString() : "-";
          return `<tr>
            <td>#${x.id}</td>
            <td>${x.status}</td>
            <td>${when}</td>
            <td>${formatMoney(x.grand_total)}</td>
            <td>${x.sold_by_user_id ?? "-"}</td>
            <td>${x.terminal_id ?? "-"}</td>
          </tr>`;
        })
        .join("");

      const html = `
        <h1>POS Receipt — Sales Report</h1>
        <div class="muted">Filter: ${label}</div>
        <div class="muted">Branch #${branchId}</div>
        <h2>Summary</h2>
        <div class="row"><span>Sales count</span><span class="strong">${list.length}</span></div>
        <div class="row"><span>Total</span><span class="strong">${formatMoney(total)}</span></div>
        <h2>Sales</h2>
        <table>
          <thead>
            <tr><th>Sale</th><th>Status</th><th>Date</th><th>Total</th><th>Cashier</th><th>Terminal</th></tr>
          </thead>
          <tbody>${rows || `<tr><td colspan="6">No sales.</td></tr>`}</tbody>
        </table>
      `;
      openPrintWindow(html, "Sales Report");
    } catch {
      alert("Unable to build receipt report.");
    }
  }

  function signOut() {
    clearPortalSession();
    navigate("/portal/login", { replace: true });
  }

  return (
    <section className="ucx-section ucx-portal-page">
      <div className="ucx-container">
        <ManufacturerNav
          title="Business Portal"
          subtitle={user?.email || "Point of Sale"}
          links={links}
          onSignOut={signOut}
        />

        <div className="ucx-portal-panel">
          <div className="ucx-portal-head">
            <div>
              <h1 className="ucx-portal-title">POS Terminal</h1>
              <p className="ucx-portal-sub">Open shift → add items → stock warning → checkout.</p>
            </div>
            <div className="ucx-portal-actions">
              <button className="ucx-btn ucx-btn-outline" type="button" onClick={refreshSales} disabled={!branchId}>
                Refresh sales
              </button>
            </div>
          </div>

          <div className="ucx-portal-grid-2">
            <div className="ucx-portal-card-large">
              <div className="ucx-portal-panel-title">Setup</div>

              <div className="ucx-portal-form" style={{ gridTemplateColumns: "minmax(0,1fr)" }}>
                <div className="ucx-portal-field">
                  <label>Branch</label>
                  <select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                    <option value="">Select branch…</option>
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>{b.name} (#{b.id})</option>
                    ))}
                  </select>
                </div>

                <div className="ucx-portal-field">
                  <label>Terminal</label>
                  <select value={terminalId} onChange={(e) => setTerminalId(e.target.value)} disabled={!branchId}>
                    <option value="">Select terminal…</option>
                    {terminals.map((t) => (
                      <option key={t.id} value={t.id}>{t.name} (#{t.id})</option>
                    ))}
                  </select>
                </div>
              </div>

              {loadingShift ? (
                <div className="ucx-muted">Loading shift…</div>
              ) : openShift?.id ? (
                <>
                  <div className="ucx-portal-badge ucx-portal-badge-success">SHIFT OPEN (#{openShift.id})</div>
                  <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button
                      className="ucx-btn ucx-btn-outline"
                      type="button"
                      onClick={onPrintActiveShiftReceipt}
                    >
                      Print shift receipt
                    </button>
                    {isSupervisor ? (
                      <button className="ucx-btn ucx-btn-outline" type="button" onClick={onPrintSupervisorReceipt}>
                        Print report (filters)
                      </button>
                    ) : null}
                  </div>

                  <div style={{ marginTop: 12 }}>
                    <div className="ucx-portal-panel-title" style={{ marginBottom: 8 }}>Shift summary</div>
                    {summaryLoading ? (
                      <div className="ucx-muted">Loading summary…</div>
                    ) : shiftSummary ? (
                      <div className="ucx-portal-card" style={{ margin: 0 }}>
                        <div className="ucx-portal-card-label">Totals</div>
                        <div className="ucx-small ucx-muted">
                          Sales: <b>{shiftSummary.salesCount ?? "-"}</b> • Net: <b>{formatMoney(shiftSummary.netTotal)}</b>
                        </div>
                      </div>
                    ) : (
                      <div className="ucx-muted">No summary yet.</div>
                    )}
                  </div>
                  <div className="ucx-portal-field" style={{ marginTop: 10 }}>
                    <label>Close note</label>
                    <input value={closingNote} onChange={(e) => setClosingNote(e.target.value)} placeholder="Optional" />
                  </div>
                  {canCloseShift ? (
                    <button className="ucx-btn ucx-btn-outline" type="button" onClick={onCloseShift}>
                      Close Shift
                    </button>
                  ) : (
                    <div className="ucx-small ucx-top-margin">You cannot close shifts (cashier restriction).</div>
                  )}
                </>
              ) : (
                <>
                  <div className="ucx-portal-badge">No open shift</div>
                  <div className="ucx-portal-field" style={{ marginTop: 10 }}>
                    <label>Open note</label>
                    <input value={openingNote} onChange={(e) => setOpeningNote(e.target.value)} placeholder="Optional" />
                  </div>
                  <button
                    className="ucx-btn ucx-btn-primary"
                    type="button"
                    onClick={onOpenShift}
                    disabled={!branchId || !terminalId || !canOpenShift}
                  >
                    Open Shift
                  </button>
                </>
              )}
            </div>

            <div className="ucx-portal-card-large">
              <div className="ucx-portal-panel-title">Recent Sales</div>
              {!canReadSales || isCashierRole ? (
                <div className="ucx-muted">Hidden for cashier (no sale read permission).</div>
              ) : salesLoading ? (
                <div className="ucx-muted">Loading…</div>
              ) : !sales.length ? (
                <div className="ucx-muted">No sales yet.</div>
              ) : (
                <div className="ucx-portal-saleslist">
                  {sales.map((s) => (
                    <div key={s.id} className="ucx-portal-sale-row">
                      <div>
                        <div className="ucx-portal-sale-title">Sale #{s.id}</div>
                        <div className="ucx-muted">
                          Status: {s.status} • Total: {s.grand_total} • {new Date(s.created_at).toLocaleString()}
                        </div>
                      </div>
                      <div className="ucx-portal-row-actions">
                        {canVoid && (
                          <button
                            className="ucx-btn ucx-btn-outline"
                            type="button"
                            onClick={() => onVoidSale(s.id)}
                          >
                            Void
                          </button>
                        )}
                        {canRefund && (
                          <button
                            className="ucx-btn ucx-btn-outline"
                            type="button"
                            onClick={() => onRefundSale(s.id)}
                          >
                            Refund
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {isSupervisor ? (
                <div style={{ marginTop: 12 }}>
                  <div className="ucx-portal-panel-title" style={{ marginBottom: 8 }}>Report filters</div>
                  <div className="ucx-portal-form" style={{ gridTemplateColumns: "repeat(3, minmax(0,1fr))" }}>
                    <div className="ucx-portal-field">
                      <label>Mode</label>
                      <select
                        value={receiptFilters.mode}
                        onChange={(e) => setReceiptFilters((p) => ({ ...p, mode: e.target.value }))}
                      >
                        <option value="shift">By shift</option>
                        <option value="terminal">By terminal</option>
                        <option value="cashier">By cashier</option>
                        <option value="date">By date range</option>
                        <option value="all">All sales</option>
                      </select>
                    </div>
                    <div className="ucx-portal-field">
                      <label>Shift ID</label>
                      <input
                        value={receiptFilters.shiftId}
                        onChange={(e) => setReceiptFilters((p) => ({ ...p, shiftId: e.target.value }))}
                        placeholder={openShift?.id ? `Default: ${openShift.id}` : "e.g. 12"}
                      />
                    </div>
                    <div className="ucx-portal-field">
                      <label>Cashier user id</label>
                      <input
                        value={receiptFilters.cashierUserId}
                        onChange={(e) => setReceiptFilters((p) => ({ ...p, cashierUserId: e.target.value }))}
                        placeholder="e.g. 3"
                      />
                    </div>
                    <div className="ucx-portal-field">
                      <label>Terminal id</label>
                      <input
                        value={receiptFilters.terminalId}
                        onChange={(e) => setReceiptFilters((p) => ({ ...p, terminalId: e.target.value }))}
                        placeholder={terminalId ? `Default: ${terminalId}` : "e.g. 1"}
                      />
                    </div>
                    <div className="ucx-portal-field">
                      <label>From</label>
                      <input
                        type="datetime-local"
                        value={receiptFilters.from}
                        onChange={(e) => setReceiptFilters((p) => ({ ...p, from: e.target.value }))}
                      />
                    </div>
                    <div className="ucx-portal-field">
                      <label>To</label>
                      <input
                        type="datetime-local"
                        value={receiptFilters.to}
                        onChange={(e) => setReceiptFilters((p) => ({ ...p, to: e.target.value }))}
                      />
                    </div>
                  </div>
                  <button className="ucx-btn ucx-btn-outline" type="button" onClick={onPrintSupervisorReceipt}>
                    Print report
                  </button>
                </div>
              ) : null}
            </div>
          </div>

          <div className="ucx-portal-card-large" style={{ marginTop: 14 }}>
            <div className="ucx-portal-panel-title">Add Items</div>

            <div className="ucx-portal-form" style={{ gridTemplateColumns: "minmax(0,1fr)" }}>
              <div className="ucx-portal-field">
                <label>Search products</label>
                <input
                   value={productQuery}
                   onChange={(e) => setProductQuery(e.target.value)}
                   onKeyDown={async (e) => {
                     if (e.key !== "Enter") return;
                     // Prevent form submits / accidental page actions
                     e.preventDefault();
                     e.stopPropagation();
                 
                     // Try barcode first; if not barcode or not found, keep default search UX
                     const didAdd = await tryResolveBarcodeAndAdd(productQuery);
                     if (!didAdd) {
                       // If not barcode or not found, do nothing special.
                       // The existing debounce search remains active.
                     }
                   }}
                   placeholder="Search product name / SKU / category… (or scan barcode + Enter)"
                   disabled={!openShift?.id || !canCreateSale}
                 />

                <div className="ucx-small">{productLoading ? "Searching…" : ""}</div>
              </div>
            </div>

            {!!productResults.length && (
              <div className="ucx-portal-results">
                {productResults.map((p) => (
                  <button key={p.id} type="button" className="ucx-portal-result" onClick={() => addProduct(p)}>
                    <div className="ucx-portal-result-name">{p.name || "Product"}</div>
                    <div className="ucx-small">ID: {p.id} • SKU: {p.ucx_sku || "-"}</div>
                  </button>
                ))}
              </div>
            )}

            {!items.length ? (
              <div className="ucx-muted">No items added.</div>
            ) : (
              <div className="ucx-portal-tablewrap">
                <table className="ucx-portal-table">
                <thead>
                  <tr>
                    <th style={{ width: "34%" }}>Product</th>
                    <th style={{ width: "14%" }}>Batch ID</th>
                    <th style={{ width: "12%" }}>Qty</th>
                    <th style={{ width: "16%" }}>Unit Price</th>
                    <th style={{ width: "12%" }}>Tax</th>
                    <th style={{ width: "12%" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => {
                    const w = stockWarnings[idx];
                    const blocked = w?.ok && w.allowed === false;
                    return (
                      <tr key={`${it.productId}-${idx}`} className={blocked ? "ucx-portal-row-blocked" : ""}>
                        <td>
                          <div>
                            <div style={{ fontWeight: 700 }}>{it.productLabel}</div>
                            {w?.ok ? (
                              <div className={blocked ? "ucx-portal-warn danger" : "ucx-portal-warn"}>
                                Stock: {w.available} {blocked ? "(insufficient)" : ""}
                              </div>
                            ) : w?.error ? (
                              <div className="ucx-portal-warn">Stock check unavailable</div>
                            ) : null}
                          </div>
                        </td>
                        <td>
                          <input
                            value={it.batchId}
                            onChange={(e) => updateItem(idx, { batchId: e.target.value })}
                            onBlur={() => checkStockForLine(idx, it)}
                            placeholder="Optional"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={it.quantity}
                            min="0"
                            step="1"
                            onChange={(e) => updateItem(idx, { quantity: e.target.value })}
                            onBlur={() => checkStockForLine(idx, { ...it, quantity: Number(it.quantity) })}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={it.unitPrice}
                            min="0"
                            step="0.01"
                            onChange={(e) => {
                              // Price is fixed for cashier; must come from POS inventory-approved value.
                              if (isCashierRole) return;
                              updateItem(idx, { unitPrice: e.target.value });
                            }}
                            disabled={isCashierRole}
                            readOnly={isCashierRole}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={it.taxRate}
                            min="0"
                            step="0.01"
                            onChange={(e) => {
                              // Tax is fixed for cashier; must come from POS inventory-approved value.
                              if (isCashierRole) return;
                              updateItem(idx, { taxRate: e.target.value });
                            }}
                            disabled={isCashierRole}
                            readOnly={isCashierRole}
                          />
                        </td>
                        <td>
                          <button type="button" className="ucx-btn ucx-btn-outline" onClick={() => removeItem(idx)}>
                            Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
             </table>
              </div>
            )}
          </div>

          <div className="ucx-portal-grid-2" style={{ marginTop: 14 }}>
            <div className="ucx-portal-card-large">
              <div className="ucx-portal-panel-title">Totals</div>
              <div className="ucx-portal-totals">
                <div className="row"><span>Subtotal</span><span>{totals.subtotal.toFixed(2)}</span></div>
                <div className="row"><span>Tax</span><span>{totals.taxTotal.toFixed(2)}</span></div>
                <div className="row">
                  <span>Discount</span>
                  <span>
                    <input type="number" min="0" step="0.01" value={discountTotal} onChange={(e) => setDiscountTotal(e.target.value)} />
                  </span>
                </div>
                <div className="row strong"><span>Grand Total</span><span>{totals.grandTotal.toFixed(2)}</span></div>
              </div>
            </div>

            <div className="ucx-portal-card-large">
              <div className="ucx-portal-panel-title">Payment</div>

              <div className="ucx-portal-form" style={{ gridTemplateColumns: "minmax(0,1fr)" }}>
                <div className="ucx-portal-field">
                  <label>Method</label>
                  <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                    {PAYMENT_METHODS.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </div>
                <div className="ucx-portal-field">
                  <label>Amount</label>
                  <input type="number" min="0" step="0.01" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} />
                </div>
              </div>

              <button className="ucx-btn ucx-btn-primary" type="button" onClick={onCheckout} disabled={!canCheckout}>
                Checkout
              </button>
              {!openShift?.id ? <div className="ucx-small ucx-top-margin">Open a shift to checkout.</div> : null}
              {!canCreateSale ? <div className="ucx-small ucx-top-margin">You cannot create sales.</div> : null}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
